import { z } from "zod";
import { type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type CreditNoteSummaryDTO } from "../../../types/dtos/api/purchase/credit-note-summary.dto";
import { type TicketDTO } from "../../../types/dtos/api/purchase/purchase-booking.dto";
import { definePersistedSlice } from "../runtime/definePersistedSlice";

/**
 * Minimal structural guard for a persisted {@link ItineraryDTO}.
 *
 * @remarks Uses permissive `catchall` so the full backend-copied payload — including
 * the injected per-segment `purchased` data — is kept as-is on disk without
 * mirroring every DTO field in Zod. Invalid or partial rows are dropped on read.
 */
const PersistableItineraryDtoSchema = z.object( {
    "id":          z.string().min( 1 ),
    "startTime":   z.string().min( 1 ),
    "endTime":     z.string().min( 1 ),
    "finalizedAt": z.string().min( 1 ),
    "timestamp":   z.number(),
    "finalized":   z.boolean(),
    "legs":        z.array( z.unknown() ),
} ).catchall( z.unknown() );

/** Minimal structural guard for a stored credit note summary (`CreditNoteSummaryDTO` shape). */
const CreditNoteSummarySchema = z.object( {
    "id":               z.string().min( 1 ),
    "creditNoteNumber": z.string().min( 1 ),
    "amountCents":      z.number(),
    "retainedFeeCents": z.number(),
    "currency":         z.string().min( 1 ),
    "createdAt":        z.string().min( 1 ),
} ).catchall( z.unknown() );

/** Minimal structural guard for a stored ticket (metadata only — files stay server-side). */
const PurchasedTicketSchema = z.object( {
    "providerTicketId":        z.string().min( 1 ),
    "externalTicketId":        z.string(),
    "externalTicketReference": z.string(),
    "preBookingId":            z.string(),
    "legId":                   z.string().optional(),
    "files":                   z.array( z.string() ).optional(),
    "ticketFormat":            z.string().nullable().
        optional(),
    "ticketData": z.string().nullable().
        optional(),
} ).catchall( z.unknown() );

/**
 * Stored ticket shape: {@link TicketDTO} fields plus optional `files` mime types.
 *
 * @remarks `files` is present when the entry came from `GET /api/purchase/bookings`
 * (`PurchasedTicketDTO`) and absent when written right after `POST /book`
 * (`BookingResponseDTO` tickets carry no file list). File contents stay server-side.
 */
export type StoredPurchasedTicket = TicketDTO & { "files"?: string[] };

/**
 * Ticket data attached to one itinerary offer segment (`offer.segments[segmentId].purchased`).
 *
 * @remarks The key is namespaced and only ever written by FlowRide — backend DTOs
 * stay untouched (extra keys survive the `catchall` itinerary guard).
 */
export interface PurchasedSegmentData {
    "preBookingId":    string;
    "offerIdentifier": string;
    "productId"?:      string | null;
    "priceCents"?:     number;
    "tickets":         StoredPurchasedTicket[];

    /** Validity window of the prebooking (ISO-8601 `from`/`to`), when the source carried it. */
    "validityPeriod"?: {
        "from": string;
        "to":   string;
    };
}

/** Display-safe payment method of a purchase (`PaymentMethodSummaryDTO` shape) — `null` fields when unknown. */
export interface PurchasedPaymentMethod {
    "provider":  string;
    "brand":     string | null;
    "wallet":    string | null;
    "cardLast4": string | null;
}

/** Purchase metadata of a bought itinerary (no payment workflow internals). */
export interface PurchasedItineraryPurchase {
    "purchaseAttemptId":   string;
    "provider":            string;
    "providerBookingId":   string | null;
    "conversationId":      string;
    "purchasedAt":         number;
    "totalPriceCents"?:    number;
    "currency"?:           string;
    "ticketFilesComplete": boolean;

    /** Payment method summary (provider, brand, wallet, card last4) — absent on entries persisted before this field existed. */
    "paymentMethod"?: PurchasedPaymentMethod | null;

    /** FlowRide booking row id — invoice download key — absent on entries persisted before this field existed. */
    "bookingId"?: string;

    /** Invoice number (`FW-YYYY-NNNNNN`) — `null` while issuance is pending, absent on older entries. */
    "invoiceNumber"?: string | null;

    /** Credit notes issued against the booking's invoice — absent on entries persisted before this field existed. */
    "creditNotes"?: CreditNoteSummaryDTO[];

    /** Tickets whose `preBookingId` matched no purchased segment (graceful fallback). */
    "unmatchedTickets": StoredPurchasedTicket[];
}

/**
 * Persisted entry for an itinerary whose tickets were emitted.
 *
 * @remarks Shape-compatible with `SavedItineraryEntry` (`itinerary`, `savedAt`,
 * route labels) so saved-list display helpers accept it directly; `savedAt`
 * carries `purchasedAt`. The itinerary keeps the base {@link ItineraryDTO}
 * format, with ticket data injected into `offer.segments[segmentId].purchased`.
 */
export interface PurchasedItineraryEntry {
    "itinerary":         ItineraryDTO;
    "type":              "purchased";
    "savedAt":           number;
    "originLabel"?:      string;
    "destinationLabel"?: string;
    "purchase":          PurchasedItineraryPurchase;
}

const PurchasedItineraryEntrySchema = z.object( {
    "itinerary":   PersistableItineraryDtoSchema,
    "type":        z.literal( "purchased" ),
    "savedAt":     z.number(),
    "originLabel": z.string().min( 1 ).
        optional(),
    "destinationLabel": z.string().min( 1 ).
        optional(),
    "purchase": z.object( {
        "purchaseAttemptId":   z.string().min( 1 ),
        "provider":            z.string().min( 1 ),
        "providerBookingId":   z.string().nullable(),
        "conversationId":      z.string().min( 1 ),
        "purchasedAt":         z.number(),
        "totalPriceCents":     z.number().optional(),
        "currency":            z.string().optional(),
        "ticketFilesComplete": z.boolean(),
        "paymentMethod":       z.object( {
            "provider":  z.string().min( 1 ),
            "brand":     z.string().nullable(),
            "wallet":    z.string().nullable(),
            "cardLast4": z.string().nullable(),
        } ).nullable().
            optional(),
        "bookingId":     z.string().optional(),
        "invoiceNumber": z.string().nullable().
            optional(),
        "creditNotes": z.array( CreditNoteSummarySchema ).
            optional(),
        "unmatchedTickets": z.array( PurchasedTicketSchema ),
    } ).catchall( z.unknown() ),
} );

/**
 * Drops corrupt purchased itinerary rows on read so one invalid entry cannot break the slice.
 *
 * @param items Raw array from storage.
 * @returns Sanitized purchased itinerary list.
 */
const sanitizePurchasedItineraries = ( items: unknown[] ): PurchasedItinerariesState => {
    return items.flatMap( ( item ) => {
        const parsed = PurchasedItineraryEntrySchema.safeParse( item );

        if ( !parsed.success ) {
            return [];
        }

        return [ parsed.data as unknown as PurchasedItineraryEntry ];
    } );
};

/**
 * Ordered purchased itineraries with invalid entries removed on read.
 *
 * @remarks Stored in IndexedDB because payloads are full {@link ItineraryDTO}
 * snapshots plus ticket data.
 */
const PurchasedItinerariesSchema = z.array( z.unknown() ).
    default( [] ).
    transform( ( items ) => sanitizePurchasedItineraries( items ) );

/** Purchased itinerary entries synced from the server (newest purchases first). */
export type PurchasedItinerariesState = PurchasedItineraryEntry[];

/** Empty list used as default (also the purge value on logout). */
export const PURCHASED_ITINERARIES_DEFAULT: PurchasedItinerariesState = [];

/**
 * Wipes v1 entries: the field rename to `provider*` is rebuilt from the
 * authenticated backend on the next sync instead of being converted in place.
 *
 * @returns Empty state.
 */
const wipePurchasedItinerariesV1 = (): PurchasedItinerariesState => PURCHASED_ITINERARIES_DEFAULT;

/** Persisted slice: purchased itineraries (lazy, IndexedDB). */
export const purchasedItinerariesSlice = definePersistedSlice<PurchasedItinerariesState>( {
    "key":            "purchasedItineraries",
    "currentVersion": 2,
    "storage":        "idb",
    "schema":         PurchasedItinerariesSchema,
    "default":        () => PURCHASED_ITINERARIES_DEFAULT,
    "migrations":     { "2": wipePurchasedItinerariesV1 },
} );
